import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownMenuItem } from "components/ui/dropdown-menu";
import { DownloadIcon } from "lucide-react";
import { saveBooksAsExcel } from "shared/services/ExportBooksService";
import { useSession } from "contexts/AuthContext";
import { getProductsByIds } from "integrations/firebase/collections";
import { useAnalytics } from "contexts/AnalyticsContext";
import { formatFirestoreTimestamp } from "shared/utils";
import { BULK_BOOKS_LIMIT } from "shared/constants";
import { SearchProductWithScore, SearchQuery } from "hooks/search/types";
import { mapIProductToSearchProduct } from "hooks/search/mappers";
import { Text } from "components/text";

interface ExportBooksDropdownItemProps {
  isSelectedAll?: boolean;
  selectedBooksCount: number;
  selectedIds: string[];
  products: SearchProductWithScore[];
  searchQuery: SearchQuery;
  fetchAllResults: () => Promise<SearchProductWithScore[]>;
}

export const ExportBooksDropdownItem = ({
  isSelectedAll,
  selectedBooksCount,
  selectedIds,
  products,
  searchQuery,
  fetchAllResults,
}: ExportBooksDropdownItemProps) => {
  const { t } = useTranslation(["books", "general"]);
  const { gaEvent } = useAnalytics();
  const { organisation } = useSession();

  const [isExporting, setIsExporting] = useState(false);

  const exportDisabled =
    selectedBooksCount === 0 ||
    selectedBooksCount > BULK_BOOKS_LIMIT ||
    isExporting;

  const handleExport = useCallback(async () => {
    if (exportDisabled) return;
    setIsExporting(true);
    try {
      gaEvent({
        type: "export_books",
        payload: { books_count: selectedBooksCount, export_type: "excel" },
      });

      const loadedById = new Map(products.map((p) => [p.id, p]));
      const missingIds = selectedIds.filter((id) => !loadedById.has(id));
      let productsToExport = products.filter((p) => selectedIds.includes(p.id));

      if (missingIds.length) {
        const extra = await getProductsByIds(organisation.id, missingIds);
        const mappedExtra = extra.map(mapIProductToSearchProduct);
        productsToExport = productsToExport.concat(mappedExtra);
      }

      if (isSelectedAll) {
        productsToExport = await fetchAllResults();
      }

      const exportData = productsToExport.map((p) => ({
        isbn: p.isbn,
        author: p.author?.join(", ") ?? "",
        title: p.title,
        productForm: p.productGroupDescription,
        publishedBy: p.publisher,
        publishedOn: formatFirestoreTimestamp(p.publishedAt),
        score: p.score,
      }));

      await saveBooksAsExcel(exportData, {
        searchType: searchQuery.type,
        searchQuery: searchQuery.query,
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    exportDisabled,
    gaEvent,
    selectedBooksCount,
    isSelectedAll,
    selectedIds,
    fetchAllResults,
    organisation,
    products,
    searchQuery.type,
    searchQuery.query,
  ]);

  return (
    <DropdownMenuItem
      className="py-2.5 px-3 cursor-pointer"
      onClick={handleExport}
      disabled={exportDisabled}
      data-testid="books-export"
      role="menuitem"
    >
      <Text as="span" variant="body" className="flex items-center">
        <DownloadIcon className="mr-2 h-4 w-4" />
        {t("books:dropdown.takeAction.item.exportBooks")}
      </Text>
    </DropdownMenuItem>
  );
};
